import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';

const RaffleScreen = () => {
  const [raffleState, setRaffleState] = useState('idle');
  const [currentName, setCurrentName] = useState('');
  const [winner, setWinner] = useState(null);
  const [names, setNames] = useState([]);
  const drumrollRef = useRef(null);
  const celebrationRef = useRef(null);
  const { lastMessage } = useWebSocket();

  // Generate display names for animation
  useEffect(() => {
    const displayNames = [
      'Alex Johnson', 'Maria Garcia', 'James Smith', 'Emma Wilson', 'Oliver Brown',
      'Sophia Martinez', 'Liam Anderson', 'Isabella Thomas', 'Noah Jackson', 'Mia White'
    ];
    setNames(displayNames);
  }, []);

  // Handle WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;

    if (lastMessage.type === 'raffle_start') {
      setRaffleState('running');
      setWinner(null);
      
      // Play drumroll (safely)
      try {
        if (drumrollRef.current) {
          drumrollRef.current.currentTime = 0;
          drumrollRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
      } catch (e) {
        console.log('Drumroll audio error:', e);
      }
    } else if (lastMessage.type === 'raffle_shuffle') {
      setCurrentName(lastMessage.current_name);
    } else if (lastMessage.type === 'raffle_winner') {
      setRaffleState('winner');
      setWinner(lastMessage.name);
      setCurrentName(lastMessage.name);
      
      // Stop drumroll and play celebration (safely)
      try {
        if (drumrollRef.current) {
          drumrollRef.current.pause();
        }
        if (celebrationRef.current) {
          celebrationRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
      } catch (e) {
        console.log('Audio control error:', e);
      }
      
      // Trigger confetti
      triggerConfetti();
    } else if (lastMessage.type === 'raffle_reset') {
      setRaffleState('idle');
      setCurrentName('');
      setWinner(null);
    } else if (lastMessage.type === 'init' && lastMessage.raffle) {
      setRaffleState(lastMessage.raffle.state || 'idle');
      if (lastMessage.raffle.winner) {
        setWinner(lastMessage.raffle.winner);
        setCurrentName(lastMessage.raffle.winner);
      }
    }
  }, [lastMessage]);

  const triggerConfetti = useCallback(() => {
    const duration = 5000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Launch from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  }, []);

  return (
    <div className="fullscreen" data-testid="raffle-screen">
      <div className="wave-background" />
      
      {/* Idle State */}
      {raffleState === 'idle' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="raffle-idle">
          <h1 className="text-5xl md:text-7xl font-bold text-white font-['Outfit'] mb-6 breathing">
            Raffle Draw
          </h1>
          <p className="text-xl md:text-2xl text-slate-400">
            Waiting for operator to start...
          </p>
        </div>
      )}

      {/* Running State - Slot Machine Animation */}
      {raffleState === 'running' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="raffle-running">
          <h2 className="text-2xl text-slate-400 mb-12 font-['Outfit']">
            Selecting Winner...
          </h2>
          
          {/* Slot machine display */}
          <div className="relative overflow-hidden bg-slate-900/80 backdrop-blur-xl border-2 border-blue-500/30 rounded-3xl px-16 py-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentName}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                transition={{ duration: 0.08 }}
                className="raffle-name text-glow"
                data-testid="raffle-current-name"
              >
                {currentName || '...'}
              </motion.div>
            </AnimatePresence>
            
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-transparent to-blue-500/10 pointer-events-none" />
          </div>

          {/* Blur effect on sides */}
          <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-[#020617] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-[#020617] to-transparent pointer-events-none" />
        </div>
      )}

      {/* Winner State */}
      {raffleState === 'winner' && winner && (
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="raffle-winner">
          {/* Winner announcement */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="text-center"
          >
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-3xl text-blue-400 font-semibold mb-6 font-['Outfit']"
            >
              WINNER
            </motion.div>
            
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
              className="raffle-name text-glow mb-8"
              data-testid="raffle-winner-name"
            >
              {winner}
            </motion.div>

            {/* Celebration visual */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center gap-4"
            >
              {['🎉', '🏆', '🎊'].map((emoji, i) => (
                <motion.span
                  key={i}
                  className="text-6xl"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}
                >
                  {emoji}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>

          {/* Radial glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl" />
          </div>
        </div>
      )}

      {/* Audio elements */}
      <audio ref={drumrollRef} src="/assets/drumroll.mp3" loop preload="auto" />
      <audio ref={celebrationRef} src="/assets/celebration.mp3" preload="auto" />
    </div>
  );
};

export default RaffleScreen;
